import { useState } from 'react'
import { includesSearchText } from '../../helpers/includesSearchText'
import { buildInventorySystemProfilePickerOptions } from './helpers/buildInventorySystemProfilePickerOptions'
import type { InventorySystemProfilesPickerProps } from './InventorySystemProfilesPicker.types'
import '../TenantGroupCodesSelect/TenantGroupCodesSelect.css'
import './InventorySystemProfilesPicker.css'

export function InventorySystemProfilesPicker({
  profiles,
  tenantGroups,
  selectedIds,
  onChange,
}: InventorySystemProfilesPickerProps) {
  const [search, setSearch] = useState('')
  const options = buildInventorySystemProfilePickerOptions(
    profiles,
    tenantGroups,
    selectedIds
  )
  const selectedOptions = options.filter((option) =>
    selectedIds.includes(option.id)
  )
  const visibleOptions = options.filter((option) =>
    includesSearchText(`${option.label} ${option.tenantGroupLabel}`, search)
  )

  function toggle(id: string) {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((selectedId) => selectedId !== id))
      return
    }
    onChange([...selectedIds, id])
  }

  if (profiles.length === 0) {
    return (
      <p className="tenant-group-codes-select-empty">
        No inventory system profiles available.
      </p>
    )
  }

  return (
    <div className="tenant-group-codes-select w-100">
      {selectedOptions.length > 0 && (
        <ul className="tenant-group-codes-select-chips">
          {selectedOptions.map((option) => (
            <li key={option.id} className="tenant-group-codes-select-chip">
              <span>
                {option.label} - {option.tenantGroupLabel}
              </span>
              <button
                type="button"
                className="tenant-group-codes-select-chip-remove"
                aria-label={`Remove ${option.label}`}
                onClick={() => toggle(option.id)}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      <input
        type="text"
        className="form-control form-control-sm"
        placeholder="Filter by ISP code, ID or tenant group"
        aria-label="Filter inventory system profiles"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <ul className="tenant-group-codes-select-list">
        {visibleOptions.length === 0 && (
          <li className="tenant-group-codes-select-empty">No matches.</li>
        )}
        {visibleOptions.map((option) => {
          const isSelected = selectedIds.includes(option.id)
          const isBlocked = !isSelected && option.blockingLabel !== null
          return (
            <li key={option.id}>
              <label
                className={`tenant-group-codes-select-option${isBlocked ? ' inventory-system-profiles-picker__option--blocked' : ''}`}
              >
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={isSelected}
                  disabled={isBlocked}
                  onChange={() => toggle(option.id)}
                />
                <span>{option.label}</span>
                <span className="inventory-system-profiles-picker__group">
                  {option.tenantGroupLabel}
                </span>
                {isBlocked && (
                  <span className="inventory-system-profiles-picker__reason">
                    {option.tenantGroupLabel} is already linked through{' '}
                    {option.blockingLabel}
                  </span>
                )}
              </label>
            </li>
          )
        })}
      </ul>
      <p className="inventory-system-profiles-picker__hint">
        Pick at least one. A venue can link to one ISP per tenant group.
      </p>
    </div>
  )
}
